import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Trophy, Medal, Crown, Flame, Users, TrendingUp, 
  Dumbbell, Moon, Apple, Droplets, ArrowLeft, 
  ChevronUp, ChevronDown, Award, Sparkles, Target
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const TeamLeaderboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.org_id && user?.role === 'org_admin') {
      fetchLeaderboard();
    } else {
      navigate('/organization');
    }
  }, [user]);

  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/organizations/${user.org_id}/team-leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data.leaderboard || []);
      setTeamStats(response.data.team_stats);
      setOrgName(response.data.organization);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      toast.error('Failed to load team leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-300" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-aura-text-muted">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/10 border-yellow-500/40';
      case 2:
        return 'bg-gradient-to-r from-gray-400/20 to-gray-300/10 border-gray-400/40';
      case 3:
        return 'bg-gradient-to-r from-amber-600/20 to-amber-700/10 border-amber-600/40';
      default:
        return 'bg-white/5 border-white/10';
    }
  };

  const getRecoveryColor = (score) => {
    if (!score) return 'text-aura-text-muted';
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-aura-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="bg-aura-surface border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/organization')}
              className="text-aura-text-muted hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-xl text-white uppercase tracking-tight">
                Team Leaderboard
              </h1>
              <p className="text-sm text-aura-text-muted">{orgName}</p>
            </div>
          </div>
          <Badge variant="outline" className="border-aura-primary text-aura-primary">
            {leaderboard.length} Athletes
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Team Stats Overview */}
        {teamStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Trophy className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{teamStats.total_team_points?.toLocaleString()}</p>
                <p className="text-xs text-aura-text-muted">Total Team Points</p>
              </CardContent>
            </Card>
            
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-6 h-6 text-aura-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{teamStats.average_aura_score}</p>
                <p className="text-xs text-aura-text-muted">Avg Aura Score</p>
              </CardContent>
            </Card>
            
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Dumbbell className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{teamStats.average_workouts_per_week}</p>
                <p className="text-xs text-aura-text-muted">Avg Workouts/Week</p>
              </CardContent>
            </Card>
            
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Moon className={`w-6 h-6 mx-auto mb-2 ${getRecoveryColor(teamStats.average_recovery_score)}`} />
                <p className={`text-2xl font-bold ${getRecoveryColor(teamStats.average_recovery_score)}`}>
                  {teamStats.average_recovery_score || '--'}
                </p>
                <p className="text-xs text-aura-text-muted">Avg Recovery</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top Performer Highlight */}
        {leaderboard[0] && (
          <Card className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-heading font-bold text-xl text-white uppercase">{leaderboard[0].name}</p>
                    <Badge className="bg-yellow-500/20 text-yellow-400">Top Performer</Badge>
                  </div>
                  <p className="text-sm text-aura-text-muted">
                    {leaderboard[0].sport} {leaderboard[0].position && `• ${leaderboard[0].position}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-heading font-bold text-3xl text-yellow-400">{leaderboard[0].aura_score}</p>
                  <p className="text-xs text-aura-text-muted">Aura Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard Table */}
        <Card className="bg-aura-surface border-white/10 overflow-hidden">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="text-white font-heading uppercase tracking-tight flex items-center gap-2">
              <Users className="w-5 h-5" />
              Athlete Rankings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-xs text-aura-text-muted uppercase tracking-wider border-b border-white/10">
              <div className="col-span-1">Rank</div>
              <div className="col-span-3">Athlete</div>
              <div className="col-span-2 text-center">Aura Score</div>
              <div className="col-span-1 text-center">Streak</div>
              <div className="col-span-1 text-center">Workouts</div>
              <div className="col-span-1 text-center">Recovery</div>
              <div className="col-span-1 text-center">Meals</div>
              <div className="col-span-2 text-center">Hydration</div>
            </div>
            
            {/* Athlete Rows */}
            <div className="divide-y divide-white/5">
              {leaderboard.map((athlete) => (
                <div 
                  key={athlete.user_id}
                  className={`px-4 py-4 ${getRankBg(athlete.rank)} hover:bg-white/5 transition-colors`}
                >
                  {/* Desktop View */}
                  <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-1">
                      {getRankIcon(athlete.rank)}
                    </div>
                    <div className="col-span-3">
                      <p className="font-medium text-white">{athlete.name}</p>
                      <p className="text-xs text-aura-text-muted">
                        {athlete.sport} {athlete.position && `• ${athlete.position}`}
                      </p>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="font-heading font-bold text-xl text-aura-primary">
                        {athlete.aura_score}
                      </span>
                    </div>
                    <div className="col-span-1 text-center">
                      {athlete.current_streak > 0 ? (
                        <Badge className="bg-orange-500/20 text-orange-400">
                          <Flame className="w-3 h-3 mr-1" />
                          {athlete.current_streak}
                        </Badge>
                      ) : (
                        <span className="text-aura-text-muted">-</span>
                      )}
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-white font-medium">{athlete.workouts_this_week}</span>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className={`font-medium ${getRecoveryColor(athlete.recovery_score)}`}>
                        {athlete.recovery_score ?? '-'}
                      </span>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-white font-medium">{athlete.meals_logged}</span>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <Progress 
                          value={Math.min(100, (athlete.hydration_oz / 100) * 100)} 
                          className="h-2 flex-1 bg-blue-500/20 [&>div]:bg-blue-500"
                        />
                        <span className="text-xs text-blue-400 w-12 text-right">
                          {athlete.hydration_oz}oz
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mobile View */}
                  <div className="md:hidden space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getRankIcon(athlete.rank)}
                        <div>
                          <p className="font-medium text-white">{athlete.name}</p>
                          <p className="text-xs text-aura-text-muted">
                            {athlete.sport} {athlete.position && `• ${athlete.position}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-heading font-bold text-xl text-aura-primary">{athlete.aura_score}</p>
                        {athlete.current_streak > 0 && (
                          <Badge className="bg-orange-500/20 text-orange-400 text-xs">
                            <Flame className="w-3 h-3 mr-1" />
                            {athlete.current_streak}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2 text-center text-xs">
                      <div className="p-2 bg-white/5 rounded">
                        <Dumbbell className="w-4 h-4 mx-auto mb-1 text-green-400" />
                        <p className="text-white font-medium">{athlete.workouts_this_week}</p>
                        <p className="text-aura-text-muted">Workouts</p>
                      </div>
                      <div className="p-2 bg-white/5 rounded">
                        <Moon className={`w-4 h-4 mx-auto mb-1 ${getRecoveryColor(athlete.recovery_score)}`} />
                        <p className={`font-medium ${getRecoveryColor(athlete.recovery_score)}`}>
                          {athlete.recovery_score ?? '-'}
                        </p>
                        <p className="text-aura-text-muted">Recovery</p>
                      </div>
                      <div className="p-2 bg-white/5 rounded">
                        <Apple className="w-4 h-4 mx-auto mb-1 text-green-400" />
                        <p className="text-white font-medium">{athlete.meals_logged}</p>
                        <p className="text-aura-text-muted">Meals</p>
                      </div>
                      <div className="p-2 bg-white/5 rounded">
                        <Droplets className="w-4 h-4 mx-auto mb-1 text-blue-400" />
                        <p className="text-white font-medium">{athlete.hydration_oz}</p>
                        <p className="text-aura-text-muted">oz</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {leaderboard.length === 0 && (
              <div className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-aura-text-muted mb-4" />
                <p className="text-white font-medium">No athletes yet</p>
                <p className="text-sm text-aura-text-muted mt-1">
                  Invite athletes to your organization to see the leaderboard
                </p>
                <Button 
                  className="mt-4 bg-aura-primary"
                  onClick={() => navigate('/organization')}
                >
                  Go to Dashboard
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Insights Section */}
        {leaderboard.length >= 3 && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-heading uppercase tracking-tight flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-aura-primary" />
                Team Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Streaks Leader */}
              {leaderboard.some(a => a.current_streak > 0) && (
                <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Flame className="w-5 h-5 text-orange-400" />
                    <span className="text-white">Longest Streak</span>
                  </div>
                  <div className="text-right">
                    <span className="text-orange-400 font-bold">
                      {Math.max(...leaderboard.map(a => a.current_streak))} days
                    </span>
                    <span className="text-aura-text-muted text-sm ml-2">
                      ({leaderboard.find(a => a.current_streak === Math.max(...leaderboard.map(x => x.current_streak)))?.name})
                    </span>
                  </div>
                </div>
              )}
              
              {/* Most Workouts */}
              <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
                <div className="flex items-center gap-3">
                  <Dumbbell className="w-5 h-5 text-green-400" />
                  <span className="text-white">Most Workouts This Week</span>
                </div>
                <div className="text-right">
                  <span className="text-green-400 font-bold">
                    {Math.max(...leaderboard.map(a => a.workouts_this_week))} workouts
                  </span>
                  <span className="text-aura-text-muted text-sm ml-2">
                    ({leaderboard.find(a => a.workouts_this_week === Math.max(...leaderboard.map(x => x.workouts_this_week)))?.name})
                  </span>
                </div>
              </div>
              
              {/* Best Recovery */}
              {leaderboard.some(a => a.recovery_score) && (
                <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Moon className="w-5 h-5 text-purple-400" />
                    <span className="text-white">Best Recovery Score</span>
                  </div>
                  <div className="text-right">
                    <span className="text-purple-400 font-bold">
                      {Math.max(...leaderboard.filter(a => a.recovery_score).map(a => a.recovery_score))}
                    </span>
                    <span className="text-aura-text-muted text-sm ml-2">
                      ({leaderboard.find(a => a.recovery_score === Math.max(...leaderboard.filter(x => x.recovery_score).map(x => x.recovery_score)))?.name})
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default TeamLeaderboardPage;
